/**
 * Community module (v1) - content aligned with Community_Module_LMS_Content_Mapping.md
 * UI flow mirrors Educator / Workplace study modules in StudyModule.js.
 */

export const COMMUNITY_CONTENT = {
  id: 'community-module',
  title: 'Community Module',
  subtitle: 'Breaking Barriers: Community Norms, Care, and STEMM Pathways',
  description:
    'For families, faith leaders, cultural organizers, alumni networks, and informal community leaders who shape norms, legitimacy, and access to opportunity.',
  courseDescriptionLong:
    'This module is designed for community members - including families, faith leaders, cultural organizers, alumni networks, and informal community leaders and elders - who shape norms, legitimacy, and access to opportunity through everyday decisions. Composite stories based on lived experiences from Breaking Barriers research invite meta-reflexivity: not to judge families or communities, but to notice how care and responsibility operate in community settings.',
  learningGoals: [
    'Recognize how barriers are produced through community norms and expectations, rather than individual choices or intentions alone.',
    'Identify where everyday decisions may unintentionally limit a South Asian woman\'s STEM career, especially at key transition points.',
    'Understand how community norms interact with institutional systems, shaping who is encouraged to stretch, who is advised to wait, and whose paths are treated as legitimate.',
    'Interrupt one patterned dynamic, so community support more reliably translates into belonging across education, work, and leadership.',
  ],
  sections: [
    {
      id: 'overview',
      title: 'Module Purpose & Learning Goals',
      content:
        '<h3>How to Use this Module</h3><p>This module is designed for <strong>community members</strong> - families, faith leaders, cultural organizers, alumni networks, and informal leaders who shape norms and access to opportunity.</p><p>Meta-reflexivity is introduced as a practice you will carry across the activities that follow.</p>',
    },
    {
      id: 'anika-story',
      title: "Aman's Case Study & Pause & Reflect",
      content:
        '<h3>Case Study: Aman</h3><p>Aman reflects <strong>patterned dynamics</strong> from our research - not a single outlier. Notice where care, concern, and expectation shape what feels possible, appropriate, or wise.</p>',
    },
    {
      id: 'barriers',
      title: 'The Five Barriers: At-A-Glance',
      content:
        '<p>These patterns appear in Aman\'s story and in what Breaking Barriers research heard repeatedly across STEMM pathways in community settings.</p>',
    },
    {
      id: 'meta-reflexivity',
      title: 'Meta-Reflexivity: Moving from Awareness to Action',
      content: '',
    },
    {
      id: 'meta-reflexive-manager',
      title: "Revisiting Aman's Story: The Meta-Reflexive Community Response",
      content: '',
    },
    {
      id: 'actions',
      title: 'Small Actions, Big Shifts - Your Role in the System',
      content:
        '<h3>Small Actions, Big Shifts</h3><p>Use the at-a-glance table as a reference for noticing - not a checklist of instructions.</p>',
    },
    {
      id: 'community-survey',
      title: 'Community Module Survey',
      content: '',
    },
  ],
};

/** Community Part 1 slide 1 - module overview video (same pattern as educator / workplace Part 1). */
export const COMMUNITY_M1_OVERVIEW_YOUTUBE_VIDEO_ID = '3XSAdXCOKqM';

export const COMMUNITY_M1_SLIDES = [
  {
    title: 'How to Use this Module',
    youtubeVideoId: COMMUNITY_M1_OVERVIEW_YOUTUBE_VIDEO_ID,
  },
];

function trimReactEnv(name) {
  const v = process.env[name];
  if (v == null) return '';
  return String(v).trim();
}

/** Part 2 - Aman case study video (env override optional). */
export const COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID =
  trimReactEnv('REACT_APP_AMAN_CASE_STUDY_YOUTUBE_VIDEO_ID') || 'pn6o_mzHSJk';

export const COMMUNITY_M2_SLIDES = [
  {
    title: 'Why This Story Matters',
    ...(COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID ? { youtubeVideoId: COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID } : {}),
    ...(!COMMUNITY_M2_AMAN_STORY_YOUTUBE_VIDEO_ID
      ? { videoPlaceholderText: 'Aman\'s story animation - video coming soon.' }
      : {}),
    lines: [
      'This module introduces the animated story of “Aman.” She isn’t meant to represent a single person, or an exceptional case. Her story reflects patterns that showed up again and again in our research.',
      'As you watch, you don’t need to decide whether Aman made the “right” choices. Instead, simply notice the moments where care, concern, and expectation show up - and how they shape what feels possible, appropriate, or wise.',
      'This isn’t a story about fault or failure. It’s an invitation to see how everyday conversations and good intentions can quietly shape the paths people take. You are not being asked to agree with a particular interpretation - only to notice what feels familiar.',
    ],
    closingLine: "Let's begin.",
  },
  { title: 'Pause & Reflect', lines: [] },
  { title: 'Pause & Reflect', lines: [] },
  { title: "Unpacking the Story: What's Really Going On?", lines: [] },
  { title: 'Why this Matters (Research Connection)', lines: [] },
];

export const COMMUNITY_M3_REFLECTION_SLIDES = [
  'As you sit with Aman\'s story, notice what comes up for you.',
  'You might feel concern, recognition, discomfort, protectiveness, or even uncertainty about how to interpret what you saw.',
  'There\'s no "right" response here. These reactions aren\'t something to fix - they\'re simply signals. Often, they point to values we care about deeply, like responsibility, care, fairness, or doing right by family and community.',
  'Take a breath. Let the reaction be there. This check-in is for your own reflection. There are no right responses, and nothing here will be evaluated',
];

export const COMMUNITY_M3_EXTERNAL_SIGNAL_SLIDE = {
  introLines: [
    'Now, gently move your focus away from Aman herself and toward the environment around her.',
    'Take a couple of minutes to notice the signals at play in the story:',
  ],
  bullets: [
    {
      label: 'Signals of approval:',
      text: 'What seems to be encouraged, praised, or treated as sensible or respectable?',
    },
    {
      label: 'Signals of caution:',
      text: 'What kinds of choices are met with questions, concern, or quiet hesitation?',
    },
    {
      label: 'Influence and authority:',
      text: 'Whose opinions shape the outcome, even without formal decisions being made?',
    },
  ],
  closingLine:
    'You don\'t need to analyze or explain yet. Just notice what feels ordinary - and how those ordinary signals shape what becomes possible.',
  unpackingSectionTitle: "Unpacking the Story: What's Really Going On?",
  unpackingLines: [
    {
      html:
        'In the video, we see Aman trying to respond carefully to what\'s around her - adjusting, weighing, choosing when to move forward and when to wait.',
    },
    {
      html:
        'What\'s easy to miss is that she isn\'t lacking clarity, motivation, or ability.',
    },
    {
      html:
        'What\'s shaping her choices is the environment she\'s moving through.',
    },
    {
      html:
        'Rather than asking <em>"Why didn\'t Aman push harder?"</em>, this section invites a different question: <strong>What signals was she responding to - and where did those signals come from?</strong>',
    },
    {
      html:
        'When care, concern, and expectation are expressed indirectly, people often adapt quietly. Not because they don\'t want to grow, but because they\'re trying to remain connected, responsible, and respected across different worlds.',
    },
  ],
};

export const COMMUNITY_M3_RESEARCH_CONNECTION_SLIDE = {
  title: 'Why this Matters (Research Connection)',
  introLines: [
    'The research below is not meant to override lived judgment or experience, but to make patterns visible that are often hard to see from the inside.',
  ],
  researchTitle: 'What the Research Shows: The Leaky Pipeline Is Not Accidental',
  researchLines: [
    {
      html:
        'Our research shows that 61% of all pathway barriers emerge among immigrant respondents, but family pressures remain constant across all groups. More importantly, these barriers are neither "random", nor personal shortcomings. They tend to intensify at specific transition points, when a person is deciding whether to stretch, relocate, take on visibility, or step into leadership.',
    },
    {
      html:
        'Across the pathway - from education into work, through early advancement, and into mid-career - these moments depend less on effort and more on endorsement, legitimacy, and support. That support doesn\'t come from institutions alone. It is shaped by families, communities, and the norms that signal what is considered wise, appropriate, or timely.',
    },
    {
      html:
        'This section is about naming those conditions, so we can better understand how everyday signals - often given with care - can accumulate into lasting patterns.',
    },
  ],
};

/** Part 2 slide 4 - leaky pipeline / barrier ecosystem figure. */
export const COMMUNITY_BARRIER_ECOSYSTEM_FIGURE = {
  src: 'images/fig_comunity.jpg',
  title: 'The Migrating Barrier Ecosystem Model',
};

export const COMMUNITY_M4_RESEARCH_INSIGHT = {
  title: 'Research Insight: Pressures Appear Across Generations',
  body: [
    'Our research found that three kinds of family and community pressure show up at almost the same rates for immigrant and Canada-born South Asian women. Expectations related to marriage were reported by 52% of immigrant women and 48% of Canada-born women. Concerns tied to family honour and reputation appeared for 47% of immigrant women and 53% of Canada-born women. Broader family and community pressure was reported by 45% of immigrant women and 55% of Canada-born women.',
    'Because these patterns appear at near-equal levels across first or second generation immigrants, they cannot be assumed to simply fade over time. Instead, they continue to be reinforced through shared expectations meant to protect and care.',
  ],
  quoteTitle: 'In Her Own Words',
  quote:
    '“So my father was extremely protective, and he was always scared that, you know, if my daughters will derail from the line, I have decided for them. What will happen? You know what answers I\'ll give to relatives, what answers I\'ll give to the society.”',
};

export const COMMUNITY_M4_GUIDED_STEPS = [
  'Review the five barriers in the table, then explore each numbered hotspot.',
  'On the next screen, optionally share which barriers feel most familiar in your context.',
  'Open “Research deep dive” in each barrier for additional framing and quotes.',
];

/** Part 3 - clear placeholder to show barrier art. */
export const COMMUNITY_BARRIERS_IMAGE_PLACEHOLDER_TEXT = '';

/** Part 3 slide 1: preview beside “Click to explore” (community-specific art). */
export const COMMUNITY_BARRIERS_INTRO_PREVIEW_IMAGE = 'images/c_1.jpg';

export const COMMUNITY_FIVE_BARRIERS = [
  {
    name: 'Family & Community Pressure',
    researchThemeTitle: 'Family & Community Pressure',
    participantQuotes: [
      'Because often where, I\'m from conservative place back in South India and growing up, I\'ve always heard this struggle… ‘we are saving money for your marriage and that\'s all we can afford.’',
    ],
    participantContext:
      'This shows up as direct advice, subtle messaging, or ongoing expectations about what a “good” daughter should prioritize. It can influence career choices, how far someone feels allowed to go, whether they can take risks, and how much emotional labor they carry to keep harmony.',
    researchStats:
      '76% (38/50) of participants discussed the Family & Community Pressure theme, and 212 quotes were coded to this theme (212/9265), representing 2.29% of all quotes.',
    researchChart: {
      participantPercent: 76,
      participantCount: 38,
      participantTotal: 50,
    },
    howItLooksInAnika:
      'Aman\'s opportunity is met first with approval, then with careful questions about travel, visibility, and timing. No one says no, but the space around her choice quietly narrows.',
    everydayMeaning:
      'Community approval acts like an informal gate. Autonomy is supported up to a point, then managed through concern, caution, and expectations about what is considered appropriate.',
    workplaceConditionTriggersIt:
      'Conversations about timing, reputation, marriage, relocation, and what “people will say.”',
    leveragePoint: 'How legitimacy is signaled: what gets publicly affirmed vs quietly questioned.',
  },
  {
    name: 'Informal Mentorship (Peer, Family, Organic)',
    researchThemeTitle: 'Informal Mentorship (Peer, Family, Organic)',
    participantQuotes: [
      'So it\'s been really helpful for me to kind of deal with some of the troubles of being in Academia by having other South Asian women I can talk to, who are senior to me, and have been able to kind of validate that some of what I\'ve experienced is based on race and gender, and not just a thing that would happen to anyone.',
    ],
    participantContext:
      'Informal mentorship is guidance that happens outside official programs: older cousins, family friends, peers, community members, and “someone who’s been through it.” It can be a major resilience factor, especially when formal mentorship does not feel culturally safe or actually helpful.',
    researchStats:
      '86.0% of participants (43/50) discussed Informal Mentorship (Peer, Family, Organic), and 253 quotes were coded to this theme (253/9265), representing 2.73% of all quotes.',
    researchChart: {
      participantPercent: 86,
      participantCount: 43,
      participantTotal: 50,
    },
    howItLooksInAnika:
      'Aman is capable and reliable, but no one actively endorses her when stretch opportunities appear. She is often left to figure things out alone.',
    everydayMeaning:
      'Without visible advocates, progress depends on self-promotion, which can feel risky when legitimacy already feels conditional. Informal guidance helps people stay confident, make decisions, and keep moving.',
    workplaceConditionTriggersIt:
      'Moments of visibility, leadership, or career stretch where endorsement could be offered but is not.',
    leveragePoint: 'Where endorsement happens - and where it stops at “you\'ll figure it out.”',
  },
  {
    name: 'Cultural & Professional Double Binds',
    researchThemeTitle: 'Cultural & Professional Double Binds',
    participantQuotes: [
      'I am often the only women of color in the meeting. I balance the amount I speak depending on my audience. I am also conscious if other women around not to look smarter than them as I don\'t want to make enemies and cause problems later for myself',
    ],
    participantContext:
      'Double binds are “no-win” situations: if you follow cultural expectations, you may be penalized professionally; if you follow professional norms, you may be judged culturally. Women describe feeling trapped between being seen as too quiet or too aggressive, too traditional or not traditional enough.',
    researchStats:
      '86.0% of participants (43/50) discussed Cultural & Professional Double Binds, and 241 quotes were coded to this theme (241/9265), representing 2.60% of all quotes.',
    researchChart: {
      participantPercent: 86,
      participantCount: 43,
      participantTotal: 50,
    },
    howItLooksInAnika:
      'Aman anticipates that future responsibilities like marriage and caregiving may conflict. She adjusts early, before any explicit barrier appears.',
    everydayMeaning:
      'Pressure and inflexibility tend to reappear at key moments, especially when leadership, visibility, or caregiving come into view.',
    workplaceConditionTriggersIt:
      'Discussions of timing, risk, caregiving, and what counts as respectable ambition.',
    leveragePoint: 'How timing, risk, and caregiving are discussed - explicitly or implicitly.',
  },
  {
    name: 'Career Suppression',
    researchThemeTitle: 'Career Suppression',
    participantQuotes: [
      'And it\'s it\'s been a challenge to this day where I don\'t speak to my in-laws because they were really holding me back in my career. They were holding me back, uh, with everything pretty much in my life. So I had to kind of really sever, sever that connection because it was so toxic.',
      'There is detriment in emphasizing \'be quiet, don\'t rock the boat\' in engineering, which is male dominated; you will get passed over if you don\'t speak out.',
    ],
    participantContext:
      'This theme includes discouragement or restriction around pursuing independent, visible, or male-dominated careers. It can appear as steering women toward “safer” fields, limiting mobility, or framing ambition as inappropriate, especially when it threatens traditional gender roles.',
    researchStats:
      '76% (38/50) of participants discussed Career Suppression, and 97 quotes were coded to this theme (97/9265), representing 1.05% of all quotes.',
    researchChart: {
      participantPercent: 76,
      participantCount: 38,
      participantTotal: 50,
    },
    howItLooksInAnika:
      'Aman senses that slowing down, pausing, or changing pace could be quietly judged, even if no one says so directly.',
    everydayMeaning:
      'When non-linear paths are framed as deviation rather than strength, people learn to limit themselves to stay respectable.',
    workplaceConditionTriggersIt:
      'Returns, pivots, pauses, or changes in direction that are treated as risky rather than resilient.',
    leveragePoint: 'What stories are celebrated: non-linear progress, pauses, pivots, and returns.',
  },
  {
    name: 'Emotional Labor & Identity Suppression',
    researchThemeTitle: 'Emotional Labor & Identity Suppression',
    participantQuotes: [
      'Pretty much my entire career I have had to code-switch or mask certain identity characteristics to be successful.',
      'At the moment I\'m silent because I also think that I am… I\'m a minority here… If I\'ll behave or react in a manner people might think that I\'m not good.',
    ],
    participantContext:
      'This is the invisible work of managing others\' comfort while hiding parts of yourself to keep peace. It can include constant tone-monitoring, careful wording, avoiding conflict, and carrying family emotions, all while trying to perform at a high level in STEMM spaces.',
    researchStats:
      '80.0% of participants (40/50) discussed Emotional Labor & Identity Suppression, and 220 quotes were coded to this theme (220/9265), representing 2.37% of all quotes.',
    researchChart: {
      participantPercent: 80,
      participantCount: 40,
      participantTotal: 50,
    },
    howItLooksInAnika:
      'Aman is praised for being sensible at home and reliable at work, yet neither space fully supports her stretching or experimenting.',
    everydayMeaning:
      'Community norms shape what feels legitimate to pursue, how belonging is maintained, and how far mobility is allowed to extend.',
    workplaceConditionTriggersIt:
      'Praise for being “sensible,” “reliable,” or “easy” that rewards staying within expected bounds.',
    leveragePoint: 'What behaviors are rewarded, praised, or held up as models.',
  },
];

export const COMMUNITY_BARRIER_CAROUSEL_SLIDES = [
  {
    imageSrc: 'images/c_2.jpg',
    alt: 'Illustration of community barriers - click numbered hotspots to read more',
    hotspots: [
      { barrierIndex: 0, left: 20, top: 10, label: '1' },
      { barrierIndex: 1, left: 85, top: 10, label: '2' },
      { barrierIndex: 2, left: 80, top: 48, label: '3' },
      { barrierIndex: 3, left: 25, top: 86, label: '4' },
      { barrierIndex: 4, left: 82, top: 86, label: '5' },
    ],
  },
];

export const COMMUNITY_BARRIER_HOTSPOTS = COMMUNITY_BARRIER_CAROUSEL_SLIDES.flatMap((slide) => slide.hotspots);

export const COMMUNITY_BARRIER_POLL_OPTIONS = [
  { value: 'community-legitimacy', label: 'Family & Community Pressure' },
  { value: 'mentorship-scarcity', label: 'Informal Mentorship (Peer, Family, Organic)' },
  { value: 'cultural-fit-filter', label: 'Cultural & Professional Double Binds' },
  { value: 'scrutiny-nonlinear-paths', label: 'Career Suppression' },
  { value: 'community-norms-autonomy', label: 'Emotional Labor & Identity Suppression' },
];

export const COMMUNITY_M5_HEADER = 'Meta-Reflexivity: Moving from Awareness to Action';

export const COMMUNITY_M5_SLIDE0_SIDE = {
  intro:
    'Apply the framework to a moment in your own community life - as a parent, auntie/uncle, elder, mentor, organizer, faith leader, or informal connector - when you or someone you know was expected to adapt, shrink, delay, or be “careful” in order to stay legitimate.',
  situationExamples: [
    'a conversation about education or career choice',
    'advice about timing, travel, or visibility',
    'endorsement or hesitation around an opportunity',
    'a response to a pause, pivot, or return',
  ],
  frameworkIntro: 'The 4-part framework invites you to surface:',
  frameworkItems: [
    'what happened,',
    'what story you are telling yourself,',
    'what feels uncomfortable - and why,',
    'what becomes possible if you let go or reframe that story.',
  ],
};

export const COMMUNITY_M5_SLIDES = [
  {
    title: 'Meta-Reflexivity Framework',
    lines: [
      // 'Let\'s apply this lens to a moment in your own community life using the Meta-Reflexivity Framework.',
      // 'Why this matters',
      // {
      //   html:
      //     'Meta-reflexivity goes beyond personal reflection. It asks: <strong>How did this way of thinking become “normal”? What rules or norms are shaping this decision?</strong>',
      // },
    ],
  },
  {
    title: 'Why this matters',
    introLine:
      'Let’s now apply this lens towards our own context using the Meta-Reflexivity Framework.',
    lines: [
      'Meta-reflexivity goes a step beyond noticing how a story makes us feel. It invites a different kind of question: What shaped the way I\'m interpreting this - and where did those expectations come from?',
      'There are no right answers. All of us carry internal stories about what it means to be responsible, respectable, ambitious, or “ready.” These stories are shaped over time - through family guidance, community norms, lived experience, and care for others. They influence how we respond in everyday moments, often without our noticing.',
      'This framework offers a pause. A chance to slow down, surface the assumptions at play, and consider where we hold influence - not to judge ourselves, but to understand how ordinary decisions can shape conditions for others.',
    ],
  },
];

/** Part 5 slide 1 - revisiting Aman (env override optional). */
export const COMMUNITY_M6_REVISITING_AMAN_YOUTUBE_VIDEO_ID =
  trimReactEnv('REACT_APP_REVISITING_AMAN_YOUTUBE_VIDEO_ID') || 'PyirlobXgMU';

export const COMMUNITY_M6_SLIDES = [
  {
    title: "Revisiting Aman's Story: A Meta-Reflexive Community Response",
    ...(COMMUNITY_M6_REVISITING_AMAN_YOUTUBE_VIDEO_ID
      ? { youtubeVideoId: COMMUNITY_M6_REVISITING_AMAN_YOUTUBE_VIDEO_ID }
      : { videoPlaceholderText: 'Revisiting Aman\'s story animation - video coming soon.' }),
    videoLayout: true,
    lines: [
      {
        html:
          'This section shows what it looks like when someone in the community moves from quietly noticing a pattern to <strong>intentionally shifting the conditions around it.</strong>',
      },
      {
        html:
          'A meta-reflexive response does not require confrontation or authority. It begins with <strong>noticing</strong>, then <strong>questioning</strong>, then <strong>acting differently in small, concrete ways.</strong>',
      },
      {
        html:
          'This is not an ideal or expected response - it is one possible way the same care can be expressed differently.',
      },
      'Watch Aman’s story again with a different ending, and then move forward to unpack what happened using a meta-reflexive lens.',
    ],
  },
  {
    title: 'The Internal Shift',
    lines: [],
    stages: [
      {
        label: 'The Observation',
        text: 'A community member notices that Aman is capable and motivated, yet consistently advised to be careful about visibility, travel, and timing - especially when opportunities involve leadership or stretch.',
      },
      {
        label: 'The Assumption',
        text: 'They catch an internal thought: “It\'s better not to invite attention.”',
      },
      {
        label: 'The Origin',
        text: 'Using meta-reflexivity, they pause and ask: Who taught me that stability should come before visibility? They recognize that these ideas didn\'t come from Aman - they come from long-standing community norms meant to protect reputation and belonging.',
      },
      {
        label: 'The Shift',
        text: 'Instead of treating hesitation as common sense, they name it as a pattern. They begin to separate care from constraint. Rather than quietly steering Aman toward delay, they choose to:',
        bullets: [
          'speak positively about her ambition',
          'still voice their care in a balanced way',
          'offer endorsement rather than caution at a key moment',
        ],
      },
    ],
    closingLines: [
      'The system around Aman doesn\'t change overnight.',
      'But the conditions do - just enough for a different outcome to become possible.',
    ],
  },
];

/** Community Part 6 - overview video first, hotspots second, commitment third. */
export const COMMUNITY_M7_OVERVIEW_YOUTUBE_VIDEO_ID = 'EmGeFHg0W6M';

export const COMMUNITY_M7_SLIDES = [
  {
    title: 'Small Actions, Big Shifts - Your Role in the System',
    youtubeVideoId: COMMUNITY_M7_OVERVIEW_YOUTUBE_VIDEO_ID,
    lines: [],
  },
  {
    title: 'At-a-Glance: Community Patterns and Points of Influence',
    lines: ['Click the numbered spots on the image to explore.'],
  },
  {
    title: 'One Intentional Shift (Optional)',
    lines: [
      'This is not a promise to fix anything - just a place to name one thing you\'re willing to examine.',
      'One way I commit to pausing, questioning, or adjusting how I show care or approval is:',
      '(If you would like to see how others have responded to this question, feel free to anonymously enter your response into the LMS prompt.)',
    ],
  },
];

export const COMMUNITY_ACTIONS_IMAGE_SRC = 'images/c_3.jpg';

/** Part 6 hotspot slide - clear placeholder to show actions art. */
export const COMMUNITY_ACTIONS_IMAGE_PLACEHOLDER_TEXT = '';

export const COMMUNITY_ACTIONS_HOTSPOTS = [
  { spotIndex: 0, left: 20, top: 10, label: '1' },
  { spotIndex: 1, left: 75, top: 20, label: '2' },
  { spotIndex: 2, left: 70, top: 65, label: '3' },
  { spotIndex: 3, left: 25, top: 86, label: '4' },
  { spotIndex: 4, left: 82, top: 86, label: '5' },
];

export const COMMUNITY_ACTIONS_TABLE_CONTENT = [
  {
    name: 'Family & Community Pressure',
    howItLooksInAnika:
      'Aman\'s opportunity is met first with approval, then with careful questions. The space around her choice quietly narrows.',
    workplaceConditionTriggersIt:
      'Community approval acts like an informal gate. Autonomy is supported up to a point, then managed through concern, caution, and expectations about what is considered appropriate.',
    leveragePoint: 'How legitimacy is signaled: what gets publicly affirmed vs quietly questioned.',
  },
  {
    name: 'Informal Mentorship (Peer, Family, Organic)',
    howItLooksInAnika:
      'Aman is capable and reliable, but no one actively endorses her when stretch opportunities appear.',
    workplaceConditionTriggersIt:
      'Without visible advocates, progress depends on self-promotion, which can feel risky when legitimacy already feels conditional.',
    leveragePoint: 'Where endorsement happens - and where it stops at “you\'ll figure it out.”',
  },
  {
    name: 'Cultural & Professional Double Binds',
    howItLooksInAnika:
      'Aman anticipates future marriage and caregiving expectations may conflict. She adjusts early, before any explicit barrier appears.',
    workplaceConditionTriggersIt:
      'Pressure and inflexibility tend to reappear at key moments, especially when leadership, visibility, or caregiving come into view.',
    leveragePoint: 'How timing, risk, and caregiving are discussed - explicitly or implicitly.',
  },
  {
    name: 'Career Suppression',
    howItLooksInAnika:
      'Aman senses that slowing down or pausing could be quietly judged, even if no one says so directly.',
    workplaceConditionTriggersIt:
      'When non-linear paths are framed as deviation rather than strength, people learn to limit themselves to stay respectable.',
    leveragePoint: 'What stories are celebrated: non-linear progress, pauses, pivots, and returns.',
  },
  {
    name: 'Emotional Labor & Identity Suppression',
    howItLooksInAnika:
      'Aman is praised for being sensible at home and reliable at work, yet neither space fully supports her stretching.',
    workplaceConditionTriggersIt:
      'Community norms shape what feels legitimate to pursue, how belonging is maintained, and how far mobility is allowed to extend.',
    leveragePoint: 'What behaviors are rewarded, praised, or held up as models.',
  },
];

export const AMAN_CASE_STUDY_VIDEO = {
  localSrc: 'videos/aman-case-study.mp4',
  remoteSrc: trimReactEnv('REACT_APP_AMAN_CASE_STUDY_VIDEO_URL'),
  poster: trimReactEnv('REACT_APP_AMAN_CASE_STUDY_VIDEO_POSTER') || 'images/e_1.jpg',
  title: 'For your own benefit - Aman\'s story',
};

export const REVISITING_AMAN_STORY_VIDEO = {
  localSrc: 'videos/revisiting-aman-story.mp4',
  remoteSrc: trimReactEnv('REACT_APP_REVISITING_AMAN_VIDEO_URL'),
  poster: trimReactEnv('REACT_APP_REVISITING_AMAN_VIDEO_POSTER') || 'images/thumb2.jpg',
  title: "Revisiting Aman's Story: A Meta-Reflexive Community Response",
};

export const COMMUNITY_META_PRIVATE_NOTE_KEY = 'lms-community-meta-private-note';
